import React, { useState } from 'react';
import { ResponsiveContainer, ResponsiveContent } from './ui/ResponsiveLayout';
import BrandSymbol from './ui/BrandSymbol';
import Button from './ui/Button';
import Input from './ui/Input';

const ResendVerification: React.FC = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setMessage('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.endofanera.ai'}/api/accounts/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setMessage(data.message);
      } else {
        setMessage(data.message || 'Failed to resend verification email');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setMessage('Failed to resend verification email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <BrandSymbol className="mx-auto h-12 w-auto mb-6" />
              <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
                Resend Verification Email
              </h2>
              <p className="text-gray-600">
                Enter your email address to receive a new verification link
              </p>
            </div>

            {success ? (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="text-green-800 text-center">
                  <p className="font-medium">Email Sent!</p>
                  <p className="text-sm mt-1">{message}</p>
                  <p className="text-sm mt-2">
                    Check your inbox and click the verification link to activate your account.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full"
                  />
                </div>

                {message && (
                  <div className={`text-center text-sm ${success ? 'text-green-600' : 'text-red-600'}`}>
                    {message}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? 'Sending...' : 'Resend Verification Email'}
                </Button>
              </form>
            )}

            <div className="text-center">
              <a
                href="/login"
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Back to Login
              </a>
            </div>
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default ResendVerification;